import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createDelegationComposerSlice, NO_ATTACHMENT_IDS } from './actions';
import { DelegationComposerEntry, DelegationComposerStore } from './types';
import { ZustandStores } from '../../utils';
import { createIndexedDBStorage } from '../../utils/indexedDBAdapter';

interface PersistedDelegationComposerEntry {
  draft: string;
}

interface PersistedDelegationComposerState {
  stateByEntry: Record<string, PersistedDelegationComposerEntry>;
}

/** Only the draft survives a reload — a queued message is a commitment against a live
 *  job and "last sent" is a per-session convenience, neither should outlive the tab.
 *  Entries with an empty draft are dropped so the store doesn't
 *  grow forever as the user opens more delegations. */
export const useDelegationComposerStore = create<DelegationComposerStore>()(
  persist(
    (set, get, store) => ({
      ...createDelegationComposerSlice(set, get, store),
    }),
    {
      name: ZustandStores.DELEGATION_COMPOSER,
      storage: createJSONStorage(() => createIndexedDBStorage()),
      partialize: (state) => ({
        stateByEntry: Object.fromEntries(
          Object.entries(state.stateByEntry)
            .filter(([, entry]) => entry.draft.length > 0)
            .map(([entryId, entry]) => [entryId, { draft: entry.draft }]),
        ),
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as
          | PersistedDelegationComposerState
          | undefined;
        if (!persisted?.stateByEntry) {
          return currentState;
        }
        const stateByEntry: Record<string, DelegationComposerEntry> =
          Object.fromEntries(
            Object.entries(persisted.stateByEntry).map(
              ([entryId, persistedEntry]) => [
                entryId,
                {
                  draft: persistedEntry.draft,
                  queuedMessage: null,
                  queuedAttachmentIds: NO_ATTACHMENT_IDS,
                  lastSentMessage: null,
                  pendingMessage: null,
                  pendingAttachmentIds: NO_ATTACHMENT_IDS,
                  hasSendFailed: false,
                },
              ],
            ),
          );
        return { ...currentState, stateByEntry };
      },
    },
  ),
);
